import {
  Box,
  Breadcrumbs,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { motion } from "framer-motion";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import {
  heroText,
  motionVariants,
  pageBackground,
} from "../../../helpers/designSystem";
import { dirTokens } from "../tokens";

/**
 * DetailShell — shared shell for Directory detail pages
 * (UserDetail, RoleDetail, and any future subject-detail surface).
 *
 * Handles the pieces every detail page kept reinventing:
 *  - pageBackground + minHeight so the hero sits on the same canvas
 *    as the parent /directory route
 *  - Back arrow + Breadcrumbs (or a single "parent" link)
 *  - Hero: optional leading avatar/icon, title, badges (Chips), and
 *    a secondary description; plus a right-side actions slot for
 *    the primary CTA(s)
 *  - Slot for the page body below the hero
 *
 * Consumers pass `body` (or children) — everything else is optional.
 *
 * Props:
 *   backTo        : string route to navigate back to (Back arrow target)
 *   parentLabel   : label for the parent link in the breadcrumb
 *   parentTo      : route for the parent link
 *   title         : hero title (string or node)
 *   avatar        : optional avatar/icon node shown left of the title
 *   badges        : array of nodes (usually Chips) rendered after the title
 *   description   : sub-hero body2 description
 *   actions       : right-side node (usually a Button cluster)
 *   children/body : page body below the hero
 */
export default function DetailShell({
  backTo,
  parentLabel,
  parentTo,
  title,
  avatar,
  badges,
  description,
  actions,
  body,
  children,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const content = body ?? children;

  return (
    <Box
      sx={{
        ...pageBackground(isDarkMode),
        mx: -3,
        mt: -3,
        px: 3,
        pt: 3,
        pb: 4,
        minHeight: "calc(100vh - 96px)",
      }}
    >
      <Box
        component={motion.div}
        initial="initial"
        animate="animate"
        variants={motionVariants?.heroEnter}
        sx={{ mb: dirTokens.section.headerGap }}
      >
        {(backTo || parentTo) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1 }}
          >
            {backTo && (
              <IconButton
                size="small"
                onClick={() => navigate(backTo)}
                aria-label={`Back to ${parentLabel || "list"}`}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            {parentTo && (
              <Breadcrumbs aria-label="breadcrumb" separator="›">
                <MuiLink
                  component={RouterLink}
                  to={parentTo}
                  underline="hover"
                  variant="body2"
                  color="text.secondary"
                >
                  {parentLabel}
                </MuiLink>
              </Breadcrumbs>
            )}
          </Stack>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "flex-start" }}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
            {avatar}
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ flexWrap: "wrap" }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    ...heroText(isDarkMode),
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {title}
                </Typography>
                {Array.isArray(badges) &&
                  badges.filter(Boolean).map((b, i) => (
                    <Box key={i} component="span" sx={{ display: "inline-flex" }}>
                      {b}
                    </Box>
                  ))}
              </Stack>
              {description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, maxWidth: 720 }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          </Stack>
          {actions && (
            <Box sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}>
              {actions}
            </Box>
          )}
        </Stack>
      </Box>

      {content}
    </Box>
  );
}
